import { useState, useEffect, useCallback } from 'react';
import { useDemoStore } from '../store/demoStore';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { api, buildEdgeImageSrc } from '../services/api';
import { t } from 'i18next';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../lib/cropImage';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Plus, Camera, X } from 'lucide-react';
import { isValidImageUrl } from '@/lib/utils';

// --- Interfaces ---
export interface AuthPerson {
  id: string;
  name: string;
  photo_url: string | null;
  notes: string | null;
  role?: string;
  location?: string;
}

export interface AuthPlate {
  id: string;
  license_plate: string;
  make: string | null;
  model: string | null;
  color: string | null;
  photo_url: string | null;
  notes: string | null;
}

/**
 * Local calendar day as YYYY-MM-DD.
 *
 * The picker asks "which day is this capture from", and the person asking is
 * standing in the store looking at a LOCAL clock. toISOString() answers in
 * UTC, which in UTC+2/+3 means the first hours of the local day report
 * yesterday. Use the same function for the default value AND for the
 * comparison - a mismatch between the two is what makes a filter silently
 * return nothing.
 */
function localDayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// --- Authorized Person Gallery Component ---
function AuthorizedPersonGallery({ personId, storeId, edgeIp, edgePort }: { personId: string; storeId: string; edgeIp?: string; edgePort?: number }) {
  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [edgeReachable, setEdgeReachable] = useState<boolean | null>(null);
  const isEdgeAvailable = edgeReachable === true;

  const [showFacePicker, setShowFacePicker] = useState(false);

  // False when the gallery list carries no parseable embeddings, which makes
  // the internal duplicate check a silent no-op. Surfaced in the UI.
  const [dupCheckUsable, setDupCheckUsable] = useState(true);

  const edgeOpts = useEdgeOpts(storeId, edgeIp, edgePort);

  // Face picker for multi-person captures. The crop is made on the Edge, so
  // there is no client-side crop maths.
  const [pickerCap, setPickerCap] = useState<any>(null);
  const [pickerImg, setPickerImg] = useState<string | null>(null);
  const [pickerFaces, setPickerFaces] = useState<any[]>([]);
  const [pickerDim, setPickerDim] = useState<{ w: number; h: number } | null>(null);
  const [pickerBusy, setPickerBusy] = useState(false);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [candLoading, setCandLoading] = useState(false);
  const [candError, setCandError] = useState<string | null>(null);

  // Filter state
  const now = new Date();
  const [filterDate, setFilterDate] = useState(localDayKey(now));
  const [filterHour, setFilterHour] = useState<number | ''>(now.getHours());
  const [filterMinBlock, setFilterMinBlock] = useState<number | ''>(Math.floor(now.getMinutes() / 10));
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Helper: cosine similarity
  function cosineSimilarity(a: number[], b: number[]): number {
    if (!a || !b || a.length !== b.length) return 0;
    let dot = 0, normA = 0, normB = 0;
    for (let i = 0; i < a.length; i++) {
      dot += a[i] * b[i];
      normA += a[i] * a[i];
      normB += b[i] * b[i];
    }
    if (normA === 0 || normB === 0) return 0;
    return dot / (Math.sqrt(normA) * Math.sqrt(normB));
  }

  // Reports the shape of the gallery payload. try/catch: an exception in a
  // useEffect takes the whole component down, including the delete buttons.
  useEffect(() => {
    try {
      if (!images.length || !images[0]) { setDupCheckUsable(true); return; }
      const sample = images[0];
      const hasKey = Object.prototype.hasOwnProperty.call(sample, 'faceEmbedding');
      const usable = hasKey && !!coerceEmbedding(sample.faceEmbedding);
      setDupCheckUsable(usable);
      if (!usable) {
        console.error(
          '[AUTH GALLERY] Internal duplicate check is INERT - the API is not returning ' +
          `parseable embeddings. keys=${Object.keys(sample).join(',')} typeof=${typeof sample.faceEmbedding}`
        );
      }
    } catch (err) {
      console.error('[AUTH GALLERY] shape probe failed (non-fatal):', err);
      setDupCheckUsable(true);
    }
  }, [images]);

  // Two questions, two thresholds. One mid-range cosine was doing both jobs,
  // so a value low enough to warn about the same person also blocked their
  // legitimate pose variants, and a malformed payload short-circuited to
  // "not a duplicate" with total confidence.
  const EXACT_DUPLICATE_SIM = 0.98;   // same image already here -> BLOCK
  const NEAR_DUPLICATE_SIM = 0.75;    // very similar person        -> ASK, never block

  const runDuplicateCheck = (candidate: number[]) => {
    const result = { usable: true, exactId: null as string | null,
                     nearId: null as string | null, bestSim: 0 };
    for (const img of images) {
      const existing = coerceEmbedding(img.faceEmbedding);
      if (!existing || existing.length !== candidate.length) { result.usable = false; return result; }
      const sim = cosineSimilarity(candidate, existing);
      if (sim > result.bestSim) result.bestSim = sim;
      if (sim >= EXACT_DUPLICATE_SIM && !result.exactId) result.exactId = img.id;
      if (sim >= NEAR_DUPLICATE_SIM && !result.nearId) result.nearId = img.id;
    }
    return result;
  };

  const confirmDuplicate = (candidate: number[]): boolean => {
    const dup = runDuplicateCheck(candidate);
    if (!dup.usable) {
      console.warn('[AUTH GALLERY] internal duplicate check skipped - gallery list has no parseable embeddings');
      return true;
    }
    if (dup.exactId) { alert("This exact image is already in this person's gallery."); return false; }
    if (dup.nearId) {
      const go = confirm(
        `This looks like a very close match to an image already in this gallery ` +
        `(similarity ${dup.bestSim.toFixed(3)}).\n\nIf it is the same person in a ` +
        `different pose, add it anyway - a wider gallery improves recognition.`
      );
      if (!go) return false;
    }
    return true;
  };

  const fetchImages = useCallback(async () => {
    if (!personId) return;
    setLoading(true);
    try {
      const data = await api.listAuthorizedPersonImages(storeId, personId);
      setImages(data.images || []);
    } catch (err) {
      console.error('Failed to fetch images:', err);
    } finally {
      setLoading(false);
    }
  }, [personId, storeId]);

  const fetchCandidates = useCallback(async () => {
    if (!edgeIp || !edgePort) return;
    setCandLoading(true);
    setCandError(null);
    try {
      // Ask the edge for captures WITHOUT a date window and do the date
      // filtering here, exactly as GuardDashboard does (see GuardDashboard.tsx
      // ~1863-1881). GuardDashboard renders today's recents correctly with
      // that shape; this screen used to send ?startDate=&endDate= to the edge
      // and trust the reply. It had NO client-side date filter at all, so
      // whatever the edge returned was taken as gospel - and today's captures
      // never made it into this list. Filtering locally makes the result
      // independent of how the edge treats the query string.
      const res = await edgeFetchOk(edgeOpts, '/captures');
      if (!res.ok) throw new Error('Failed to fetch captures');
      const caps = await res.json();

      // People only. The edge stamps each capture with the camera's CURRENT
      // role, which is not always 'access_person' - a camera set to
      // poi_person / poi_mix stamps those instead. The old hard filter
      // (`role !== 'access_person'`) therefore emptied this picker the moment
      // a camera's role was anything else, while GuardDashboard kept showing
      // the same captures because it has no role filter by default
      // (faceFilterType === 'all'). Vehicles are the only rows that cannot
      // become an authorised face, so exclude those and keep every person
      // role - including any we have not seen before.
      const isVehicleRole = (role: any) => /vehicle/i.test(String(role || ''));
      const filtered = caps.filter((item: any) => {
        if (isVehicleRole(item.role)) return false;
        const d = new Date(item.timestamp);
        // Date filter, LOCAL day - same helper as the default value.
        if (filterDate && localDayKey(d) !== filterDate) return false;
        if (filterHour !== '' && d.getHours() !== Number(filterHour)) return false;
        if (filterMinBlock !== '' && Math.floor(d.getMinutes() / 10) !== Number(filterMinBlock)) return false;
        return true;
      });
      setCandidates(filtered);
    } catch (err: any) {
      setCandError(err.message || 'Failed to load');
      setCandidates([]);
    } finally {
      setCandLoading(false);
    }
  }, [edgeOpts.hostname, edgeIp, edgePort, filterDate, filterHour, filterMinBlock]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  useEffect(() => {
    if (showFacePicker) {
      fetchCandidates();
    }
  }, [showFacePicker, filterDate, filterHour, filterMinBlock, fetchCandidates, refreshTrigger]);

  useEffect(() => {
    const checkEdge = async () => {
      if (!edgeIp || !edgePort) {
        setEdgeReachable(false);
        return;
      }
      try {
        const res = await edgeFetchOk(edgeOpts, '/ping', { signal: AbortSignal.timeout(2000) });
        setEdgeReachable(res.ok);
      } catch {
        setEdgeReachable(false);
      }
    };
    checkEdge();
  }, [edgeIp, edgePort]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      e.target.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Image must be smaller than 10MB.');
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const uploadRes = await api.uploadPhoto(dataUrl);
      const photoUrl = uploadRes.url;

      const embedRes = await api.post('/api/pois/compute-embedding', { photoUrl });
      if (!embedRes.embedding || !Array.isArray(embedRes.embedding)) {
        alert('No full face detected. Try a frontal photo — profiles are rejected.');
        setUploading(false);
        e.target.value = '';
        return;
      }

      // Internal duplicate: the same face already in this gallery.
      if (!confirmDuplicate(embedRes.embedding)) {
        setUploading(false);
        e.target.value = '';
        return;
      }

      // 2. Global duplicate: check if this face matches another authorized person
      const duplicateCheck = await api.checkAuthorizedPersonDuplicate(embedRes.embedding);
      if (duplicateCheck.duplicate && duplicateCheck.existing) {
        const shouldContinue = confirm(
          `This face matches an existing authorized person (${duplicateCheck.existing.name}).\n\nDo you still want to add it to this person's gallery?`
        );
        if (!shouldContinue) {
          setUploading(false);
          e.target.value = '';
          return;
        }
      }

      await api.addAuthorizedPersonImage(storeId, personId, {
        photo_url: photoUrl,
        face_embedding: embedRes.embedding,
      });

      await fetchImages();
      alert('Image added successfully!');
    } catch (err: any) {
      console.error('Upload error:', err);
      alert('Failed to upload image: ' + (err.message || 'unknown error'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemove = async (imageId: string) => {
    if (!confirm('Remove this image?')) return;
    try {
      await api.deleteAuthorizedPersonImage(storeId, personId, imageId);
      await fetchImages();
    } catch (err: any) {
      alert('Failed to remove image: ' + (err.message || err));
    }
  };

  // Show the capture with every detected face drawn on it. Enrols nothing.
  const openFacePicker = async (cap: any) => {
    setPickerBusy(true);
    setPickerError(null);
    try {
      const frameRes = await edgeFetchOk(edgeOpts, `/captures/${cap.id}/frame`);
      if (!frameRes.ok) {
        throw new Error(
          `Edge returned ${frameRes.status} for the capture image. The edge agent must be ` +
          'on v0.4.9.4.9h or newer to enrol from Recent.'
        );
      }
      const dataUrl = await blobToDataUrl(await frameRes.blob());

      // The Edge already has this frame on disk, so ask about it by id rather
      // than uploading the JPEG again.
      let faces: any[] = [];
      let busy = false;
      for (let attempt = 0; attempt < 3; attempt++) {
        const detRes = await edgeFetchAny(edgeOpts, '/detect-faces', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ capture_id: cap.id }),
        });
        if (detRes.status === 503) {
          busy = true;
          await new Promise((r) => setTimeout(r, 1200));
          continue;
        }
        if (!detRes.ok) throw new Error('Face detection failed on the edge.');
        const body = await detRes.json();
        faces = Array.isArray(body.faces) ? body.faces : [];
        busy = false;
        break;
      }
      // Every attempt was refused because the recogniser was restarting. That
      // is a missing answer, NOT an empty room.
      if (busy) {
        throw new Error('The edge is busy restarting its recogniser. Wait a moment and try again.');
      }

      setPickerImg(dataUrl);
      setPickerFaces(faces);
      setPickerCap(cap);
    } catch (err: any) {
      setPickerError(err.message || 'Failed to load the capture');
    } finally {
      setPickerBusy(false);
    }
  };

  // Enrol exactly the one face the operator pointed at. The crop is made on
  // the Edge from the original frame at natural size and aspect, so it holds
  // one face by construction and the checked vector is the stored vector.
  const enrollWithFace = async (cap: any, bbox: number[]) => {
    setPickerBusy(true);
    setPickerError(null);
    try {
      const cropRes = await edgeFetchAny(edgeOpts, '/capture-crop', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ capture_id: cap.id, bbox }),
      });
      if (cropRes.status === 503) {
        throw new Error('The edge was busy restarting. Wait a moment and click the face again.');
      }
      if (!cropRes.ok) {
        const body = await cropRes.json().catch(() => null);
        const detail = body?.error || (await cropRes.text().catch(() => '')) || '';
        throw new Error(
          `Edge refused the crop (HTTP ${cropRes.status})` +
          (detail ? `: ${detail}` : ' - no detail returned.')
        );
      }
      const { image, embedding, faces_in_crop } = await cropRes.json();
      if (!image) throw new Error('The edge returned no cropped image for that selection.');
      if (!embedding) {
        throw new Error(
          `The crop was produced but the recogniser read 0 faces from it (faces_in_crop=${faces_in_crop ?? 0}).`
        );
      }

      if (!confirmDuplicate(embedding)) return;

      const dup = await api.checkAuthorizedPersonDuplicate(embedding);
      if (dup.duplicate && dup.existing) {
        const go = confirm(
          `This face matches an existing authorized person (${dup.existing.name}).\n\n` +
          `If that is the same person enrolled twice, cancel and clean up the roster.`
        );
        if (!go) return;
      }

      const { url } = await api.uploadPhoto(image);
      await api.addAuthorizedPersonImage(storeId, personId, {
        photo_url: url,
        face_embedding: embedding,
      });

      await fetchImages();
      setPickerCap(null);
      setPickerImg(null);
      setPickerFaces([]);
      alert('Image added.');
    } catch (err: any) {
      setPickerError(err.message || 'Failed to add the image');
    } finally {
      setPickerBusy(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading gallery...</p>;
  }

  return (
    <div className="space-y-2 border-t pt-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold">Gallery ({images.length}/20)</h4>
        <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowFacePicker(!showFacePicker)}
              disabled={!isEdgeAvailable}
            >
              <Camera className="w-3 h-3 mr-1" />
              Add from Recent
              {!isEdgeAvailable && <span className="ml-1 text-[10px] text-muted-foreground">(edge not reachable)</span>}
            </Button>
          <label className="cursor-pointer">
            <span className={`text-xs px-2 py-1 rounded border ${uploading ? 'opacity-50' : 'hover:bg-accent'}`}>
              {uploading ? 'Uploading...' : 'Upload Image'}
            </span>
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {!dupCheckUsable && (
        <p className="text-[11px] text-amber-600 dark:text-amber-400 border border-amber-500/40 rounded px-2 py-1">
          Some duplicate checks are unavailable for this gallery. Adding an image
          that is already here may be allowed.
        </p>
      )}

      {images.length === 0 ? (
        <p className="text-xs text-muted-foreground">No gallery images yet.</p>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          {images.map((img: any) => (
            <div key={img.id} className="relative group aspect-square">
              {(img.displayPhotoUrl || img.photoUrl) ? (
                <img
                  src={img.displayPhotoUrl || img.photoUrl}
                  alt="Gallery"
                  className="w-full h-full object-cover rounded"
                />
              ) : (
                <div className="w-full h-full bg-muted rounded flex items-center justify-center text-xs">N/A</div>
              )}
              <button
                type="button"
                onClick={() => handleRemove(img.id)}
                className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Remove image"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Face picker. Boxes are positioned as percentages of the frame, so there
          is no natural<->DOM coordinate conversion anywhere. */}
      {pickerCap && (
        <div className="fixed inset-0 z-[10000] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-background rounded-lg p-4 max-w-3xl w-full space-y-3">
            <div>
              <p className="text-sm font-semibold">Which face do you want to add?</p>
              <p className="text-xs text-muted-foreground">
                This capture can contain more than one person. Pick the right one.
              </p>
            </div>

            {pickerError && <p className="text-xs text-destructive">{pickerError}</p>}

            <div className="relative w-full">
              <img
                src={pickerImg || ''}
                alt="Capture to pick from"
                className="w-full rounded"
                onLoad={(e) => setPickerDim({
                  w: e.currentTarget.naturalWidth,
                  h: e.currentTarget.naturalHeight,
                })}
              />
              {pickerDim && pickerDim.w > 0 && pickerFaces.map((f: any, i: number) => {
                const [x, y, w, h] = f.bbox;
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={pickerBusy}
                    onClick={() => enrollWithFace(pickerCap, f.bbox)}
                    className="absolute border-2 border-primary bg-primary/10 hover:bg-primary/30 rounded-sm"
                    style={{
                      left: `${(x / pickerDim.w) * 100}%`,
                      top: `${(y / pickerDim.h) * 100}%`,
                      width: `${(w / pickerDim.w) * 100}%`,
                      height: `${(h / pickerDim.h) * 100}%`,
                    }}
                    title={
                      `Face ${i + 1} - detector score ${(f.score ?? 0).toFixed(2)}` +
                      (f.quality === 'inconclusive' ? ' - geometry inconclusive' : '')
                    }
                  >
                    <span className="absolute -top-1 -left-1 bg-primary text-primary-foreground text-[10px] px-1 rounded">
                      {i + 1}
                    </span>
                  </button>
                );
              })}
            </div>

            {pickerFaces.length === 0 && !pickerBusy && (
              <p className="text-xs text-muted-foreground">No face was detected in this capture.</p>
            )}
            {pickerFaces.length === 1 && (
              <p className="text-xs text-muted-foreground">One face detected - tap box 1 to add it.</p>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setPickerCap(null); setPickerError(null); }}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Recent Faces Picker */}
      {showFacePicker && (
        <div className="border rounded p-2 space-y-2 mt-2">
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="text-xs">Date</label>
              <input
                type="date"
                value={filterDate}
                onChange={e => { setFilterDate(e.target.value); setRefreshTrigger(prev => prev + 1); }}
                className="w-full p-1 border rounded text-xs bg-background"
              />
            </div>
            <div>
              <label className="text-xs">Hour</label>
              <select
                value={filterHour}
                onChange={e => { setFilterHour(e.target.value === '' ? '' : parseInt(e.target.value)); setRefreshTrigger(prev => prev + 1); }}
                className="w-full p-1 border rounded text-xs bg-background"
              >
                <option value="">All</option>
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>{String(i).padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs">10-min block</label>
              <select
                value={filterMinBlock}
                onChange={e => { setFilterMinBlock(e.target.value === '' ? '' : parseInt(e.target.value)); setRefreshTrigger(prev => prev + 1); }}
                className="w-full p-1 border rounded text-xs bg-background"
              >
                <option value="">All</option>
                <option value="0">0‑9 min</option>
                <option value="1">10‑19 min</option>
                <option value="2">20‑29 min</option>
                <option value="3">30‑39 min</option>
                <option value="4">40‑49 min</option>
                <option value="5">50‑59 min</option>
              </select>
            </div>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => {
                const now = new Date();
                setFilterDate(localDayKey(now));
                setFilterHour(now.getHours());
                setFilterMinBlock(Math.floor(now.getMinutes() / 10));
                setRefreshTrigger(prev => prev + 1);
              }}
            >
              Reset
            </Button>
          </div>

          {candLoading ? (
            <p className="text-xs text-muted-foreground">Loading captures…</p>
          ) : candError ? (
            <p className="text-xs text-destructive">{candError}</p>
          ) : candidates.length === 0 ? (
            <p className="text-xs text-muted-foreground border border-dashed rounded p-3 text-center">
              No captures match the current filters.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto">
              {candidates.map((cap: any) => (
                <div key={cap.id} className="border rounded p-1.5">
                  <img
                    {...edgeImgProps(edgeOpts, `/captures/${cap.id}`)}
                    alt="Capture"
                    className="w-full h-24 object-contain rounded bg-muted"
                  />
                  <p className="text-[10px] text-muted-foreground truncate">{cap.camera_name}</p>
                  {/* The grid shows the face, so it must also say how many
                      people that frame actually held. */}
                  {(cap.face_count ?? 1) > 1 && (
                    <p className="text-[10px] text-amber-600 dark:text-amber-400 font-medium">
                      {cap.face_count} faces in frame — pick on next screen
                    </p>
                  )}
                  <Button
                    size="sm"
                    type="button"
                    className="w-full h-6 text-xs mt-1"
                    onClick={() => openFacePicker(cap)}
                  >
                    Add
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Authorized Persons Manager (with embedding support) ---
export function AuthorizedPersonsManager({ storeId, onItemClick, edgeIp, edgePort }: { storeId: string, onItemClick?: (item: AuthPerson) => void, edgeIp?: string, edgePort?: number }) {
  const [persons, setPersons] = useState<AuthPerson[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState<AuthPerson | null>(null);
  const [formData, setFormData] = useState({ name: '', notes: '', role: 'staff', location: '' });
  const [photoUrl, setPhotoUrl] = useState('');
  const [faceEmbedding, setFaceEmbedding] = useState<number[] | null>(null);
  const [search, setSearch] = useState('');

  // Cropper state
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [showCropper, setShowCropper] = useState(false);

  // --- Face picker state ---
  const [showFacePicker, setShowFacePicker] = useState(false);
  const [candidates, setCandidates] = useState<any[]>([]);
  const [candLoading, setCandLoading] = useState(false);
  const [candError, setCandError] = useState<string | null>(null);

  // Filter state (default to last 10 minutes)
  const now = new Date();
  const [filterDate, setFilterDate] = useState(localDayKey(now));
  const [filterHour, setFilterHour] = useState<number | ''>(now.getHours());
  const [filterMinBlock, setFilterMinBlock] = useState<number | ''>(Math.floor(now.getMinutes() / 10));
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const fetchPersons = useCallback(async () => {
    try {
      const data = await api.getAuthorizedPersons(storeId);
      setPersons(data);
    } catch (err) {
      console.error(err);
    }
  }, [storeId]);

  useEffect(() => { fetchPersons(); }, [fetchPersons]);

  const handleAddOrUpdate = async () => {
    if (!formData.name.trim()) return;
    try {
      let finalPhotoUrl = photoUrl;
      let finalFaceEmbedding = faceEmbedding; // Use a local variable

      // If there is a cropped image, upload it
      if (imageSrc && croppedAreaPixels) {
        const croppedImage = await getCroppedImg(imageSrc, croppedAreaPixels);
        let finalCroppedImage = croppedImage;
        if (finalCroppedImage.startsWith('blob:')) {
          const response = await fetch(finalCroppedImage);
          const blob = await response.blob();
          finalCroppedImage = await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.readAsDataURL(blob);
          });
        }
        // Upload to server
        const { url } = await api.uploadPhoto(finalCroppedImage);
        finalPhotoUrl = url;

        // Compute embedding from the uploaded image
        if (!finalFaceEmbedding) {
          const embedRes = await api.post('/api/pois/compute-embedding', { photoUrl: finalPhotoUrl });
          if (embedRes.embedding && Array.isArray(embedRes.embedding)) {
            finalFaceEmbedding = embedRes.embedding;
            setFaceEmbedding(finalFaceEmbedding); // Update state for UI
          }
        }
      }

      // --- DUPLICATE CHECK ---
      // Only check for new persons (not editing existing)
      if (!editingPerson && finalFaceEmbedding) {
        try {
          const duplicateCheck = await api.checkAuthorizedPersonDuplicate(finalFaceEmbedding);
          if (duplicateCheck.duplicate) {
            const shouldContinue = confirm(
              `This face appears to match an existing authorized person (${duplicateCheck.existing?.name || 'Unknown'}).\n\nDo you still want to add this person?`
            );
            if (!shouldContinue) return;
          }
        } catch (err) {
          console.warn('[AuthorizedPersonsManager] Duplicate check failed:', err);
          // Continue anyway – duplicate check is not critical
        }
      }

      const payload: any = {
        name: formData.name,
        // v0.4.9.4.6c10 — `null`, not `undefined`.
        //
        // stores.js PATCH /:id/authorized-persons/:personId does
        //     if (photo_url !== undefined) { fields.push('photo_url = …') }
        // so an empty photoUrl produced `undefined`, JSON.stringify DROPPED
        // the key entirely, and the backend read "field absent, do not
        // touch". Clearing the photo was indistinguishable from never
        // opening the photo field, and the old image stayed forever.
        //
        // `null` is an explicit "set this column to NULL" and is what the
        // Remove Photo button below needs.
        //
        // The POST path is unaffected: stores.js INSERT does
        // `photo_url || null` server-side, so null and undefined were
        // already identical there.
        photo_url: finalPhotoUrl || null,
        notes: formData.notes || undefined,
        role: formData.role,
        location: formData.location,
      };
      if (finalFaceEmbedding) payload.face_embedding = finalFaceEmbedding;

      console.log('[iSentinel360] AuthorizedPerson payload:', {
        ...payload,
        face_embedding: finalFaceEmbedding ? `array length ${finalFaceEmbedding.length}` : 'null',
      });

      if (editingPerson) {
        await api.updateAuthorizedPerson(storeId, editingPerson.id, payload);
      } else {
        await api.addAuthorizedPerson(storeId, payload);
      }
      setModalOpen(false);
      setEditingPerson(null);
      setFormData({ name: '', notes: '', role: 'staff', location: '' });
      setPhotoUrl('');
      setFaceEmbedding(null);
      setImageSrc(null);
      fetchPersons();
    } catch (err) {
      console.error('Save failed:', err);
      alert('Failed to save');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Delete this authorized person?')) {
      await api.deleteAuthorizedPerson(storeId, id);
      fetchPersons();
    }
  };

  const fetchCandidates = useCallback(async () => {
  if (!storeId) return;
  setCandLoading(true);
  setCandError(null);
  try {
    // Build URL with filters
    let url = `http://${edgeIp}:${edgePort}/captures`;
    const params = new URLSearchParams();
    if (filterDate) {
      const start = new Date(filterDate + 'T00:00:00Z');
      const end = new Date(filterDate + 'T23:59:59Z');
      params.set('startDate', start.toISOString());
      params.set('endDate', end.toISOString());
    } else {
      const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      params.set('startDate', sevenDaysAgo.toISOString());
    }
    if (params.toString()) url += `?${params.toString()}`;
    
    const res = await fetch(url);
    if (!res.ok) throw new Error('Failed to fetch captures');
    const caps = await res.json();
    
    // Filter: only Access Control captures (role === 'access_person')
    // Also apply hour and minute-block filters
    const filtered = caps.filter((item: any) => {
      if (item.role !== 'access_person') return false;
      const d = new Date(item.timestamp);
      if (filterHour !== '' && d.getHours() !== Number(filterHour)) return false;
      if (filterMinBlock !== '' && Math.floor(d.getMinutes() / 10) !== Number(filterMinBlock)) return false;
      return true;
    });
    
    setCandidates(filtered);
  } catch (err: any) {
    setCandError(err.message || 'Failed to load');
    setCandidates([]);
  } finally {
    setCandLoading(false);
  }
}, [storeId, edgeIp, edgePort, filterDate, filterHour, filterMinBlock]);

  useEffect(() => {
    if (showFacePicker) {
      fetchCandidates();
    }
  }, [showFacePicker, filterDate, filterHour, filterMinBlock, fetchCandidates, refreshTrigger]);

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => {
          setModalOpen(true);
          setEditingPerson(null);
          setFormData({ name: '', notes: '', role: 'staff', location: '' });
          setPhotoUrl('');
          setFaceEmbedding(null);
          setImageSrc(null);
    // v0.4.9.4.6c10 — Reset the cropper state on EVERY open path.
    // setShowCropper was only ever called on upload (true) and after a
    // successful crop (false). A cropper left open by a modal the user
    // dismissed therefore stayed open, and the NEXT modal rendered
    //     <Cropper image={imageSrc!} … />      with imageSrc === null
    // i.e. a black pane, and Crop & Save called
    //     getCroppedImg(null, …) -> loadImage(null) -> img.src = '' -> onerror
    //     "Failed to load image"  (cropImage.ts:20), uncaught because that
    // button had no try/catch. Same class of bug as the 401: stale state
    // surviving a lifecycle it should not have.
    setShowCropper(false);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
        }}>
          <Plus className="w-4 h-4 mr-1" /> Add Person
        </Button>
      </div>

      <Input placeholder="Search by name..." value={search} onChange={(e) => setSearch(e.target.value)} />

      <div className="border rounded divide-y max-h-60 overflow-y-auto">
        {persons.length === 0 && <p className="p-3 text-sm text-muted-foreground">No authorized persons</p>}
        {persons
          .filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
          .map(person => (
            <div key={person.id} className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50" onClick={() => onItemClick?.(person)}>
              <div className="flex items-center gap-3">
                {person.photo_url && isValidImageUrl(person.photo_url) ? (
                  <img src={person.photo_url} className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-xs">N/A</div>
                )}
                <div>
                  <p className="font-medium">{person.name}</p>
                  {person.notes && <p className="text-xs text-muted-foreground">{person.notes}</p>}
                </div>
              </div>
              <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                <Button size="sm" variant="outline" onClick={() => {
                  setEditingPerson(person);
                  setFormData({ name: person.name, notes: person.notes || '', role: person.role || 'staff', location: person.location || '' });
                  setPhotoUrl(person.photo_url || '');
                  setImageSrc(null);
    // v0.4.9.4.6c10 — Reset the cropper state on EVERY open path.
    // setShowCropper was only ever called on upload (true) and after a
    // successful crop (false). A cropper left open by a modal the user
    // dismissed therefore stayed open, and the NEXT modal rendered
    //     <Cropper image={imageSrc!} … />      with imageSrc === null
    // i.e. a black pane, and Crop & Save called
    //     getCroppedImg(null, …) -> loadImage(null) -> img.src = '' -> onerror
    //     "Failed to load image"  (cropImage.ts:20), uncaught because that
    // button had no try/catch. Same class of bug as the 401: stale state
    // surviving a lifecycle it should not have.
    setShowCropper(false);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
                  setModalOpen(true);
                }}>Edit</Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(person.id)}>Remove</Button>
              </div>
            </div>
          ))}
      </div>

      {/* Modal for Add/Edit */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-white dark:bg-gray-900 w-[calc(100%-2rem)] sm:w-[calc(100%-4rem)] md:max-w-2xl lg:max-w-4xl max-h-[90vh] overflow-y-auto z-[9999]">
          <DialogHeader>
            <DialogTitle>{editingPerson ? 'Edit Person' : 'Add Person'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
            </div>
            <div>
              <Label>Role</Label>
              <Input value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })} />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} />
            </div>
            <div>
              <Label>Notes</Label>
              <textarea className="w-full p-2 border rounded bg-background" rows={2} value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} />
            </div>
            <div>
              <Label>Photo</Label>
              {!showCropper ? (
                <div className="space-y-2">
                  {photoUrl && isValidImageUrl(photoUrl) && <img src={photoUrl} className="w-32 h-32 object-cover rounded" />}
                  <div className="flex gap-2">
                    <label className="cursor-pointer bg-primary/10 hover:bg-primary/20 text-primary text-center py-2 rounded-md text-sm">
                      Upload Photo
                      <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        if (e.target.files?.[0]) {
                          const reader = new FileReader();
                          reader.onload = () => {
                            setImageSrc(reader.result as string);
                            setShowCropper(true);
                            setFaceEmbedding(null);
                          };
                          reader.readAsDataURL(e.target.files[0]);
                        }
                      }} />
                    </label>

                    {/* v0.4.9.4.6c10 — Remove the main photo.
                        Previously the only way to drop a bad photo was to
                        delete the whole person and recreate them, and the
                        gallery's per-image delete buttons did not touch
                        `authorized_persons.photo_url` at all.
                        This clears the local photo AND the face embedding
                        that was computed from it, and the payload now sends
                        `null` for photo_url so stores.js actually sets the
                        column to NULL instead of skipping the field.
                        Label is hardcoded English to match the adjacent
                        hardcoded "Upload Photo" in this same block. */}
                    {photoUrl && (
                      <button
                        type="button"
                        onClick={() => {
                          setPhotoUrl('');
                          setFaceEmbedding(null);
                          setImageSrc(null);
                          setShowCropper(false);
                          setCroppedAreaPixels(null);
                        }}
                        className="bg-destructive/10 hover:bg-destructive/20 text-destructive text-center py-2 px-3 rounded-md text-sm"
                      >
                        Remove Photo
                      </button>
                    )}

                    {/* Add recent faces picker */}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative w-full aspect-square max-h-[50vh] bg-black rounded-md overflow-hidden">
                    <Cropper image={imageSrc!} crop={crop} zoom={zoom} aspect={1} onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, area) => setCroppedAreaPixels(area)} />
                  </div>
                  <input type="range" min={1} max={3} step={0.01} value={zoom} onChange={e => setZoom(Number(e.target.value))} className="w-full" />
                  <Button type="button" onClick={async () => {
                    const cropped = await getCroppedImg(imageSrc!, croppedAreaPixels);
                    let final = cropped;
                    if (final.startsWith('blob:')) {
                      const resp = await fetch(final);
                      const blob = await resp.blob();
                      final = await new Promise<string>(resolve => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.readAsDataURL(blob);
                      });
                    }
                    setPhotoUrl(final);
                    // Compute embedding if edge agent is reachable
                    if (edgeIp && edgePort) {
                      try {
                        const embedUrl = `http://${edgeIp}:${edgePort}/compute-embedding-cropped`;
                        console.log('[iSentinel360] Computing embedding →', embedUrl);
                        const embedRes = await fetch(embedUrl, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ image: final }),
                        });
                        console.log('[iSentinel360] Edge response status:', embedRes.status);
                        if (embedRes.ok) {
                          const embedData = await embedRes.json();
                          if (embedData && Array.isArray(embedData.embedding)) {
                            console.log('[iSentinel360] Embedding received, length:', embedData.embedding.length);
                            setFaceEmbedding(embedData.embedding);
                          } else {
                            console.warn('[iSentinel360] Edge response missing embedding array:', embedData);
                          }
                        } else {
                          const errBody = await embedRes.text().catch(() => '');
                          console.warn('[iSentinel360] Edge embedding call failed:', embedRes.status, errBody);
                        }
                      } catch (err) { console.warn('[iSentinel360] Embedding compute failed:', err); }
                    } else {
                      console.warn('[iSentinel360] edgeIp/edgePort not provided — skipping embedding computation. Person will be saved WITHOUT a face_embedding.');
                    }
                    setShowCropper(false);
                    setImageSrc(null);
                  }}>Crop & Save</Button>
                </div>
              )}
            </div>
            {editingPerson && (
              <AuthorizedPersonGallery
                personId={editingPerson.id}
                storeId={storeId}
                edgeIp={edgeIp}
                edgePort={edgePort}
                key={editingPerson.id}
              />
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={handleAddOrUpdate}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Authorized Plates Manager (unchanged) ---
/** Accept either a real array or a JSON string. Returns null if neither. */
function coerceEmbedding(raw: any): number[] | null {
  if (Array.isArray(raw)) return raw as number[];
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as number[]) : null;
    } catch {
      return null;
    }
  }
  return null;
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Edge coordinates for the capture helpers.
 *
 * NOTE the asymmetry, which is the whole point: buildEdgeImageSrc() behaves
 * completely differently depending on whether a hostname is present.
 *
 *   with hostname -> primary https://hostname/...,  ipHttps https://edgeIp/...
 *   without       -> primary https://edgeIp/...   (NO PORT), ipHttps null
 *
 * The "without" branch targets port 443 and cannot satisfy a hostname-only
 * certificate, so it fails everywhere. So the hostname is fetched here and
 * the caller decides which transport to use - never the other way round.
 */
function useEdgeOpts(storeId: string, edgeIp?: string, edgePort?: number) {
  const [hostname, setHostname] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!storeId) return;
    // MUST be getStores(), not getStore(id).
    //
    // GET /api/stores/:id is gated by canManageStore (stores.js) and answers
    // 403 for a guard who is not a store admin — which is exactly who uses
    // this screen. The list endpoint has no such per-store gate, and
    // GuardDashboard has always read the hostname from it. Reading it from
    // the gated endpoint left hostname null, buildEdgeImageSrc then returned
    // https://<ip> with no port, and a *.isentinel360 SAN certificate can
    // never satisfy that — so every tile fell through to the placeholder.
    api.getStores()
      .then((list: any) => {
        if (cancelled) return;
        const found = Array.isArray(list) ? list.find((x: any) => x?.id === storeId) : null;
        const raw = typeof found?.hostname === 'string' ? found.hostname.trim() : '';
        setHostname(raw === '' ? null : raw);
      })
      .catch(() => { if (!cancelled) setHostname(null); });
    return () => { cancelled = true; };
  }, [storeId]);
  return { hostname, edgeIp, edgePort };
}

/**
 * Two edge fetch strategies, because they answer different questions.
 *
 * edgeFetchOk   — keep trying rungs until one answers res.ok.
 *                 Used for the capture LIST, /ping and /frame. If the
 *                 store's HTTPS hostname resolves but its proxy answers
 *                 404/502, we must still fall back to the IP, or the
 *                 picker renders nothing. Getting this wrong is what broke
 *                 the gallery on BOTH desktop and mobile: a helper that
 *                 returns on any response never reaches the IP rung.
 *
 * edgeFetchAny  — return the FIRST response, ok or not.
 *                 Used only for /capture-crop, where a non-ok body is the
 *                 message we need to show ("that crop still contains more
 *                 than one face"). edgeFetchWithFallback would swallow it
 *                 into null.
 *
 * Same two helpers in GuardDashboard so the screens cannot drift.
 */
async function edgeFetchOk(
  opts: { hostname: string | null; edgeIp?: string; edgePort?: number },
  path: string,
  init: RequestInit = {}
) {
  const urls: string[] = [];
  if (opts.hostname) urls.push(`https://${opts.hostname}${path}`);
  if (opts.edgeIp) urls.push(`https://${opts.edgeIp}${path}`);
  if (opts.edgeIp && opts.edgePort) urls.push(`http://${opts.edgeIp}:${opts.edgePort}${path}`);

  let last: Response | null = null;
  for (const url of urls) {
    try {
      const res = await fetch(url, init);
      if (res.ok) return res;
      last = res;              // remember it, but keep trying the IP rung
    } catch {
      // unreachable rung — DNS, TLS, refused, blocked as mixed content
    }
  }
  if (last) return last;       // nothing worked; let the caller read the error
  throw new Error('The edge is unreachable from this device.');
}

async function edgeFetchAny(
  opts: { hostname: string | null; edgeIp?: string; edgePort?: number },
  path: string,
  init: RequestInit = {}
) {
  const urls: string[] = [];
  if (opts.hostname) urls.push(`https://${opts.hostname}${path}`);
  if (opts.edgeIp) urls.push(`https://${opts.edgeIp}${path}`);
  if (opts.edgeIp && opts.edgePort) urls.push(`http://${opts.edgeIp}:${opts.edgePort}${path}`);

  for (const url of urls) {
    try {
      return await fetch(url, init);
    } catch {
      // unreachable rung
    }
  }
  throw new Error('The edge is unreachable from this device.');
}

/** Same rule for an <img>: the ladder only when we have a hostname. */
function edgeImgProps(opts: { hostname: string | null; edgeIp?: string; edgePort?: number }, path: string) {
  const placeholder =
    'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100" ' +
    'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"%3E%3Crect x="2" y="2" ' +
    'width="20" height="20" rx="2" ry="2"%3E%3C/rect%3E%3Cpath d="M7 2v20M17 2v20M2 12h20M2 7h5M2 17h5M17 17h5M17 7h5"%3E%3C/path%3E%3C/svg%3E';

  if (opts.hostname && opts.edgeIp) {
    const imgSrc = buildEdgeImageSrc(opts, path);
    return {
      src: imgSrc.primary,
      onError: (e: React.SyntheticEvent<HTMLImageElement>) => {
        const cur = e.currentTarget.src;
        if (imgSrc.ipHttps && cur !== imgSrc.ipHttps) { e.currentTarget.src = imgSrc.ipHttps; return; }
        if (imgSrc.fallback && cur !== imgSrc.fallback) { e.currentTarget.src = imgSrc.fallback; return; }
        e.currentTarget.src = placeholder;
      },
    };
  }

  return {
    src: opts.edgeIp && opts.edgePort ? `http://${opts.edgeIp}:${opts.edgePort}${path}` : placeholder,
    onError: (e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.src = placeholder; },
  };
}

export function AuthorizedPlatesManager({ storeId, onItemClick }: { storeId: string, onItemClick?: (item: AuthPlate) => void }) {
  const { recentFaces } = useDemoStore();
  const [plates, setPlates] = useState<AuthPlate[]>([]);
  const [newPlate, setNewPlate] = useState('');
  const [newMake, setNewMake] = useState('');
  const [newModel, setNewModel] = useState('');
  const [newColor, setNewColor] = useState('');
  const [newPlatePhoto, setNewPlatePhoto] = useState('');
  const [newPlateNotes, setNewPlateNotes] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPlate, setEditPlate] = useState('');
  const [editMake, setEditMake] = useState('');
  const [editModel, setEditModel] = useState('');
  const [editColor, setEditColor] = useState('');
  const [editPhoto, setEditPhoto] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [search, setSearch] = useState('');
  const [showFaceSelector, setShowFaceSelector] = useState(false);
  const [faceFilterDate, setFaceFilterDate] = useState('');
  const [faceFilterHour, setFaceFilterHour] = useState<number | null>(null);
  const [faceFilterMinBlock, setFaceFilterMinBlock] = useState<number | null>(null);
  const [faceFilterType, setFaceFilterType] = useState<'all' | 'person' | 'vehicle'>('vehicle');
  const [plateImageSrc, setPlateImageSrc] = useState<string | null>(null);
  const [plateCrop, setPlateCrop] = useState({ x: 0, y: 0 });
  const [plateZoom, setPlateZoom] = useState(1);
  const [plateCroppedAreaPixels, setPlateCroppedAreaPixels] = useState<any>(null);
  const [showPlateCropper, setShowPlateCropper] = useState(false);

  const fetchPlates = useCallback(async () => {
    try {
      const data = await api.getAuthorizedPlates(storeId);
      setPlates(data);
    } catch (err) {
      console.error(err);
    }
  }, [storeId]);

  useEffect(() => { fetchPlates(); }, [fetchPlates]);

  const handleAdd = async () => {
    if (!newPlate.trim()) return;
    let finalPhotoUrl = newPlatePhoto;
    if (finalPhotoUrl.startsWith('data:')) {
      try {
        const { url } = await api.uploadPhoto(finalPhotoUrl);
        finalPhotoUrl = url;
      } catch (err) {
        alert('Failed to upload photo');
        return;
      }
    }
    try {
      await api.addAuthorizedPlate(storeId, {
        license_plate: newPlate,
        make: newMake || undefined,
        model: newModel || undefined,
        color: newColor || undefined,
        photo_url: finalPhotoUrl || undefined,
        notes: newPlateNotes || undefined,
      });
      setNewPlate(''); setNewMake(''); setNewModel(''); setNewColor(''); setNewPlatePhoto(''); setNewPlateNotes('');
      fetchPlates();
    } catch (err) {
      alert('Failed to add plate');
    }
  };

  const handleDelete = async (plateId: string) => {
    if (!confirm('Delete this authorized vehicle?')) return;
    try {
      await api.deleteAuthorizedPlate(storeId, plateId);
      fetchPlates();
    } catch (err) {
      alert('Failed to delete plate');
    }
  };

  return (
    <div className="space-y-2">
      <div className="h-1" />
      Add New
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Input placeholder="License Plate" value={newPlate} onChange={(e) => setNewPlate(e.target.value)} className="flex-1" />
        <Input placeholder="Make (opt)" value={newMake} onChange={(e) => setNewMake(e.target.value)} />
        <Input placeholder="Model (opt)" value={newModel} onChange={(e) => setNewModel(e.target.value)} />
        <Input placeholder="Color (opt)" value={newColor} onChange={(e) => setNewColor(e.target.value)} />
      </div>
      <div className="flex gap-2 items-center">
        <label className="flex-1 cursor-pointer bg-primary/10 hover:bg-primary/20 text-primary text-center py-2 rounded-md text-sm font-medium transition-colors">
          {t('admin.uploadPhoto') || 'Upload Photo'}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                const reader = new FileReader();
                reader.onload = () => {
                  setPlateImageSrc(reader.result as string);
                  setShowPlateCropper(true);
                };
                reader.readAsDataURL(e.target.files[0]);
              }
            }}
          />
        </label>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={() => {
            setFaceFilterType('vehicle');
            setShowFaceSelector(true);
          }}
          title={t('admin.pickImage')}
        >
          <Camera className="w-4 h-4" />
        </Button>
        
      </div>
      {showPlateCropper && plateImageSrc && (
        <div className="flex flex-col gap-2 mt-2">
          <div className="relative w-full aspect-square max-h-[50vh] bg-black rounded-md overflow-hidden">
            <Cropper
              image={plateImageSrc}
              crop={plateCrop}
              zoom={plateZoom}
              aspect={1}
              onCropChange={setPlateCrop}
              onZoomChange={setPlateZoom}
              onCropComplete={(_, croppedAreaPixels) => setPlateCroppedAreaPixels(croppedAreaPixels)}
            />
          </div>
          <div className="flex items-center gap-2 px-1">
            <span className="text-xs text-muted-foreground">Zoom</span>
            <input
              type="range"
              min={1} max={3} step={0.01}
              value={plateZoom}
              onChange={(e) => setPlateZoom(Number(e.target.value))}
              className="flex-1 h-2 rounded-full appearance-none bg-gray-200 dark:bg-gray-700 cursor-pointer"
            />
          </div>
          <button
            type="button"
            className="w-full bg-primary hover:bg-primary/90 text-white py-2 rounded-md font-medium"
            onClick={async () => {
              try {
                const croppedImage = await getCroppedImg(plateImageSrc, plateCroppedAreaPixels);
                let finalCroppedImage = croppedImage;
                if (finalCroppedImage.startsWith('blob:')) {
                  const response = await fetch(finalCroppedImage);
                  const blob = await response.blob();
                  finalCroppedImage = await new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                  });
                }
                // If editing, set the edit photo; otherwise set the new plate photo
                if (editingId) {
                  setEditPhoto(finalCroppedImage);
                } else {
                  setNewPlatePhoto(finalCroppedImage);
                }
                setShowPlateCropper(false);
                setPlateImageSrc(null);
              } catch (e) {
                console.error(e);
              }
            }}
          >
            {t('admin.cropSave') || 'Crop & Save'}
          </button>
        </div>
      )}
      {/* ── Face selector grid (full width, below the top row) ── */}
      {showFaceSelector && (
        <div className="border p-2 rounded space-y-2 mt-2">
          {/* Filters */}
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="text-xs">{t('guard.filterDate')}</label>
              <input
                type="date"
                value={faceFilterDate}
                onChange={(e) => setFaceFilterDate(e.target.value)}
                className="w-full p-1 border rounded text-xs"
              />
            </div>
            <div>
              <label className="text-xs">{t('guard.filterHour')}</label>
              <select
                value={faceFilterHour ?? ''}
                onChange={(e) => setFaceFilterHour(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full p-1 border rounded text-xs"
              >
                <option value="">All</option>
                {Array.from({length: 24}, (_, i) => (
                  <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs">{t('guard.filterMinBlock')}</label>
              <select
                value={faceFilterMinBlock ?? ''}
                onChange={(e) => setFaceFilterMinBlock(e.target.value ? parseInt(e.target.value) : null)}
                className="w-full p-1 border rounded text-xs"
              >
                <option value="">All</option>
                <option value="0">0‑9 min</option>
                <option value="1">10‑19 min</option>
                <option value="2">20‑29 min</option>
                <option value="3">30‑39 min</option>
                <option value="4">40‑49 min</option>
                <option value="5">50‑59 min</option>
              </select>
            </div>
            <div>
              <label className="text-xs">{t('guard.filterType')}</label>
              <select
                value={faceFilterType}
                onChange={(e) => setFaceFilterType(e.target.value as any)}
                className="w-full p-1 border rounded text-xs"
              >
                <option value="all">All</option>
                <option value="person">Persons</option>
                <option value="vehicle">Vehicles</option>
              </select>
            </div>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => {
                setFaceFilterDate('');
                setFaceFilterHour(null);
                setFaceFilterMinBlock(null);
                setFaceFilterType('vehicle');
              }}
            >
              {t('common.clear')}
            </Button>
          </div>

          {/* Filtered faces grid */}
          {(() => {
            const filtered = recentFaces.filter(face => {
              if (faceFilterDate) {
                const d = face.timestamp.toISOString().split('T')[0];
                if (d !== faceFilterDate) return false;
              }
              if (faceFilterHour !== null && face.timestamp.getHours() !== faceFilterHour) return false;
              if (faceFilterMinBlock !== null) {
                const m = face.timestamp.getMinutes();
                if (Math.floor(m / 10) !== faceFilterMinBlock) return false;
              }
              if (faceFilterType === 'person' && face.sex === 'V') return false;
              if (faceFilterType === 'vehicle' && face.sex !== 'V') return false;
              return true;
            });

            if (filtered.length === 0) {
              return <p className="text-xs text-muted-foreground text-center py-2">{t('guard.noFacesMatch')}</p>;
            }

            return (
              <div className="grid grid-cols-3 gap-2 max-h-40 overflow-y-auto">
                {filtered.map(face => (
                  <img
                    key={face.id}
                    src={face.imageUrl}
                    alt="Recent Face"
                    className="w-full h-24 object-contain rounded cursor-pointer hover:ring-2 ring-primary bg-muted"
                    onClick={() => {
                      if (editingId) {
                        setEditPhoto(face.imageUrl);
                      } else {
                        setNewPlatePhoto(face.imageUrl);
                      }
                      setShowFaceSelector(false);
                    }}
                  />
                ))}
              </div>
            );
          })()}
        </div>
      )}
      {newPlatePhoto && (
        <div className="mt-2">
          <img src={newPlatePhoto} className="w-full h-32 object-contain rounded-md bg-muted" />
        </div>
      )}
      <Input placeholder="Notes (opt)" value={newPlateNotes} onChange={(e) => setNewPlateNotes(e.target.value)} className="flex-1" />
      <Button 
      onClick={handleAdd} size="sm">Add
      </Button>
      <div className="mt-2">
      Search Currents
      </div>
      {/* Search box for plates */}
      <Input
          placeholder="Search by plate..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mt-2"
        />

      <div className="border rounded divide-y max-h-60 overflow-y-auto">
        {plates.length === 0 && <p className="p-3 text-sm text-muted-foreground">No authorized plates</p>}
        {plates
          .filter(p => 
            p.license_plate.toLowerCase().includes(search.toLowerCase()) ||
            (p.make || '').toLowerCase().includes(search.toLowerCase()) ||
            (p.model || '').toLowerCase().includes(search.toLowerCase())
          )
          .map(plate => (
          <div
            key={plate.id}
            className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50"
            onClick={() => onItemClick?.(plate)}
          >
            {editingId === plate.id ? (
              <div className="flex-1 space-y-2" onClick={(e) => e.stopPropagation()}>
                <Input placeholder="License Plate" value={editPlate} onChange={(e) => setEditPlate(e.target.value)} />
                <div className="grid grid-cols-2 gap-2">
                  <Input placeholder="Make" value={editMake} onChange={(e) => setEditMake(e.target.value)} />
                  <Input placeholder="Model" value={editModel} onChange={(e) => setEditModel(e.target.value)} />
                  <Input placeholder="Color" value={editColor} onChange={(e) => setEditColor(e.target.value)} />
                </div>
                <div className="flex gap-2 items-center">
                  <label className="flex-1 cursor-pointer bg-primary/10 hover:bg-primary/20 text-primary text-center py-2 rounded-md text-sm font-medium transition-colors">
                    {t('admin.uploadPhoto') || 'Upload Photo'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          const reader = new FileReader();
                          reader.onload = () => {
                            setPlateImageSrc(reader.result as string);
                            setEditingId(plate.id);        // ← add this line
                            setShowPlateCropper(true);
                          };
                          reader.readAsDataURL(e.target.files[0]);
                        }
                      }}
                    />
                  </label>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setFaceFilterType('vehicle');
                      setShowFaceSelector(true);
                      setEditingId(plate.id); // so face click goes to edit
                    }}
                    title={t('admin.pickImage')}
                  >
                    <Camera className="w-4 h-4" />
                  </Button>
                </div>
                {editPhoto && (
                  <img src={editPhoto} className="w-full h-32 object-contain rounded-md bg-muted mt-2" />
                )}
                <Input placeholder="Notes" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
                <div className="flex gap-2">
                  <Button size="sm" onClick={async () => {
                    try {
                      await api.updateAuthorizedPlate(storeId, plate.id, {
                        license_plate: editPlate || undefined,
                        make: editMake || undefined,
                        model: editModel || undefined,
                        color: editColor || undefined,
                        photo_url: editPhoto || undefined,
                        notes: editNotes || undefined,
                      });
                      setEditingId(null);
                      fetchPlates();
                    } catch (err) { alert('Update failed'); }
                  }}>Save</Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  {plate.photo_url ? (
                    <img src={plate.photo_url} className="w-12 h-12 object-cover rounded" />
                  ) : (
                    <div className="w-12 h-12 rounded bg-muted flex items-center justify-center text-xs">N/A</div>
                  )}
                  <div>
                    <p className="font-mono font-bold">{plate.license_plate}</p>
                    <p className="text-sm">{plate.make} {plate.model} – {plate.color}</p>
                    {plate.notes && <p className="text-xs text-muted-foreground">{plate.notes}</p>}
                  </div>
                </div>
                <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" variant="outline" onClick={() => {
                    setEditingId(plate.id);
                    setEditPlate(plate.license_plate);
                    setEditMake(plate.make || '');
                    setEditModel(plate.model || '');
                    setEditColor(plate.color || '');
                    setEditPhoto(plate.photo_url || '');
                    setEditNotes(plate.notes || '');
                  }}>Edit</Button>
                  <Button size="sm" variant="destructive" onClick={() => handleDelete(plate.id)}>Remove</Button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}